import { useState, useEffect } from "react";
import { useLocation, Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTheme } from "@/components/ui/theme-provider";
import { Sun, Moon, AlertCircle, Loader2 } from "lucide-react";
import logoBlack from "@assets/commentt_ai_logo_black.svg";
import { apiRequest } from "@/lib/queryClient";

const schema = z.object({
  firstName: z.string().min(1, "First name is required").max(100),
  lastName: z.string().min(1, "Last name is required").max(100),
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type InviteSignupForm = z.infer<typeof schema>;

interface Props {
  params: { token: string };
}

export default function SignupInvite({ params }: Props) {
  const { token } = params;
  const [, setLocation] = useLocation();
  const [serverError, setServerError] = useState("");
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();

  const { data: inviteInfo, isLoading: inviteLoading, error: inviteError } = useQuery<{ orgName: string; orgSlug: string; targetEmail: string | null }>({
    queryKey: ["/api/auth/invite-token", token],
    queryFn: async () => {
      const res = await fetch(`/api/auth/invite-token/${token}`);
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.message || "Invalid invite link");
      }
      return res.json();
    },
    retry: false,
  });

  const form = useForm<InviteSignupForm>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: "", lastName: "", email: "", password: "" },
  });

  useEffect(() => {
    if (inviteInfo?.targetEmail) {
      form.setValue("email", inviteInfo.targetEmail);
    }
  }, [inviteInfo?.targetEmail]);

  const signupMutation = useMutation({
    mutationFn: (data: InviteSignupForm) =>
      apiRequest("POST", "/api/auth/signup-invite", { ...data, token }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      setLocation("/");
    },
    onError: (err: any) => {
      let message = "Sign up failed. Please try again.";
      try {
        const colonIdx = (err.message as string)?.indexOf(": ");
        if (colonIdx !== -1) {
          const bodyText = (err.message as string).slice(colonIdx + 2);
          const body = JSON.parse(bodyText);
          if (body?.message) message = body.message;
        }
      } catch {}
      setServerError(message);
    },
  });

  function onSubmit(data: InviteSignupForm) {
    setServerError("");
    signupMutation.mutate(data);
  }

  if (inviteLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (inviteError || !inviteInfo) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border">
          <Link href="/">
            <img src={logoBlack} alt="Commentt.ai" className="h-3.5 dark:invert" />
          </Link>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-sm space-y-4 text-center">
            <AlertCircle className="h-10 w-10 mx-auto text-destructive" />
            <h1 className="text-2xl font-semibold">Invite link invalid</h1>
            <p className="text-sm text-muted-foreground">
              This invite link has expired or already been used.
            </p>
            <Link href="/login" className="text-sm text-foreground font-medium hover:underline">
              Go to sign in
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b border-border">
        <Link href="/">
          <img src={logoBlack} alt="Commentt.ai" className="h-3.5 dark:invert" />
        </Link>
        <button
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          className="p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm space-y-6">
          <div className="space-y-1 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Join {inviteInfo.orgName}
            </h1>
            <p className="text-sm text-muted-foreground">
              Create your account to accept the invitation
            </p>
          </div>

          {serverError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{serverError}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First name</FormLabel>
                      <FormControl>
                        <Input placeholder="Jane" autoComplete="given-name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last name</FormLabel>
                      <FormControl>
                        <Input placeholder="Smith" autoComplete="family-name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        autoComplete="email"
                        readOnly={!!inviteInfo?.targetEmail}
                        className={inviteInfo?.targetEmail ? "bg-muted cursor-not-allowed" : ""}
                        {...field}
                      />
                    </FormControl>
                    {inviteInfo?.targetEmail && (
                      <p className="text-xs text-muted-foreground">
                        This invite was sent to this email address and cannot be changed.
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="At least 8 characters" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={signupMutation.isPending}>
                {signupMutation.isPending ? "Creating account..." : "Accept invitation"}
              </Button>
            </form>
          </Form>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link href="/login" className="text-foreground font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
